/** /account: password change and subscription cancellation. */

import { cancelSubscription, changePassword } from "./api/client";
import type { SubscriptionState } from "./api/types";
import { announce } from "./lib/announce";
import { closeDialog, dialogById } from "./lib/dialog";
import { find, findAll, setMessage } from "./lib/dom";
import { byteLength, formValue, isBusy, setBusy } from "./lib/forms";
import { ApiError, errorMessage } from "./lib/http";

const DEFAULT_MIN_PASSWORD = 8;

/** Why a new password cannot be used ("" when it can). */
export function passwordProblem(
  password: string,
  confirmation: string,
  minimum: number,
): string {
  if (byteLength(password) < minimum) return `Minimum ${minimum} characters.`;
  if (password !== confirmation) return "Passwords do not match.";
  return "";
}

/** The minimum length the page states (the input's minlength). */
export function minimumLength(form: HTMLFormElement): number {
  const input = find(form, "input[name=new_password]", HTMLInputElement);
  const value = Number.parseInt(input?.getAttribute("minlength") ?? "", 10);
  return Number.isNaN(value) ? DEFAULT_MIN_PASSWORD : value;
}

function initPasswordForm(): void {
  const form = find(document, "[data-password-form]", HTMLFormElement);
  if (form === null) return;
  const error = find(form, "[data-form-error]", HTMLElement);
  const success = find(form, "[data-form-success]", HTMLElement);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (isBusy(form)) return;
    if (success) success.hidden = true;
    const oldPassword = formValue(form, "old_password");
    const newPassword = formValue(form, "new_password");
    const problem = passwordProblem(
      newPassword,
      formValue(form, "new_password_confirm"),
      minimumLength(form),
    );
    if (error) setMessage(error, problem);
    if (problem !== "") return;
    setBusy(form, true);
    changePassword(oldPassword, newPassword).then(
      () => {
        setBusy(form, false);
        form.reset();
        if (success) success.hidden = false;
      },
      (failure: unknown) => {
        setBusy(form, false);
        let message = errorMessage(failure);
        if (
          failure instanceof ApiError &&
          failure.code === "incorrect_old_password"
        ) {
          message = "Sorry, this password is invalid. Please try again.";
        } else if (failure instanceof ApiError && failure.status === 401) {
          message = "Your session has expired. Please sign in again.";
        }
        if (error) setMessage(error, message);
      },
    );
  });
}

function showSubscription(state: SubscriptionState): void {
  for (const element of findAll(
    document,
    "[data-subscription-state]",
    HTMLElement,
  )) {
    element.hidden = element.dataset["subscriptionState"] !== state;
  }
}

function initCancel(): void {
  const dialog = dialogById("cancel-dialog");
  if (dialog === null) return;
  const confirm = find(dialog, "[data-cancel-confirm]", HTMLButtonElement);
  const error = find(dialog, "[data-form-error]", HTMLElement);
  dialog.addEventListener("close", () => {
    if (error) setMessage(error, "");
  });
  confirm?.addEventListener("click", () => {
    if (confirm.disabled) return;
    confirm.disabled = true;
    cancelSubscription().then(
      (state) => {
        confirm.disabled = false;
        closeDialog(dialog);
        showSubscription(state);
        announce(
          state === "canceled"
            ? "Your subscription has been canceled."
            : "The state of your subscription has changed.",
        );
        find(document, "[data-subscription]", HTMLElement)?.focus();
      },
      (failure: unknown) => {
        confirm.disabled = false;
        const message =
          failure instanceof ApiError && failure.code === "billing_unavailable"
            ? "We could not cancel your subscription right now. Please try again later or contact us: contact@docs.zone."
            : errorMessage(failure);
        if (error) setMessage(error, message);
      },
    );
  });
}

export function initAccount(): void {
  initPasswordForm();
  initCancel();
}
